// Data types for Wattelzce Demo - Hierarchical Model
// Hierarchy: Site → Zone → Module → Equipment

export interface ModuleUsage {
  usage_id: string;
  date: string;
  zone_id: string;
  module_id: string;
  measurement_level: string; // "Zone" or "Équipement"
  site_type: string;
  site_name: string;
  zone_name: string;
  equipment_name: string;
  equipment_category: string;
  duration_min: number;
  energy_kwh: number;
  cost_eur: number;
  co2_kg: number;
}

export interface ModuleDim {
  module_id: string;
  module_name: string;
  zone_id: string;
  site_type: string;
  site_name: string;
  zone_name: string;
  measurement_level: string;
  installation_type: string;
  equipment_name: string;
  equipment_category: string;
  product_family: string;
}

export interface ZoneDim {
  zone_id: string;
  site_type: string;
  site_name: string;
  zone_name: string;
}

export interface DemoKpis {
  totalEnergy: number;
  totalCost: number;
  totalCo2: number;
  activeModules: number;
  avgCostPerKwh: number;
  totalDuration: number;
}

export interface EnergyByDate {
  date: string;
  energy: number;
  cost: number;
  co2: number;
  isForecast?: boolean;
}

export interface ConsumptionBySector {
  sector: string;
  zone_id: string;
  zoneLabel: string; // "Zone 1", "Zone 2", etc.
  energy: number;
  cost: number;
  co2: number;
}

export interface ConsumptionByDeviceType {
  device_type: string;
  energy: number;
  cost: number;
  co2: number;
  percentage: number;
}

export interface TopModule {
  module_id: string;
  module_name: string;
  sector: string;
  device_type: string;
  energy: number;
  cost: number;
  co2: number;
}

// Hierarchical drill-down types
export type DrillLevel = 'zone' | 'module' | 'equipment';

export interface HierarchyItem {
  id: string;
  name: string;
  displayLabel: string;
  energy: number;
  cost: number;
  co2: number;
  level: DrillLevel;
  parentId?: string;
  parentName?: string;
  hasChildren: boolean;
}

export interface FilterOptions {
  sectors: string[];
  deviceTypes: string[];
  siteTypes: string[];
  siteNames: string[];
  dateRange: { min: string; max: string };
}

export interface DemoFilters {
  sector?: string;      // maps to zone_name
  deviceType?: string;  // maps to equipment_category
  siteType?: string;
  siteName?: string;
  startDate?: string;
  endDate?: string;
  measurementLevel?: string; // "Zone" or "Équipement"
}

import { translate } from '../i18n';

// Import CSV data - NEW hierarchical files
import factModuleUsageRaw from '../data/fact_module_usage_hierarchy_demo.csv?raw';
import dimModuleRaw from '../data/dim_module_hierarchy_demo.csv?raw';
import dimZoneRaw from '../data/dim_zone_hierarchy_demo.csv?raw';

// Log CSV import status for debugging
console.log('CSV imports loaded:', {
  factModuleUsageRaw: factModuleUsageRaw ? `${factModuleUsageRaw.length} chars` : 'MISSING',
  dimModuleRaw: dimModuleRaw ? `${dimModuleRaw.length} chars` : 'MISSING',
  dimZoneRaw: dimZoneRaw ? `${dimZoneRaw.length} chars` : 'MISSING',
});

// Parse CSV data
function parseCSV<T>(csv: string): T[] {
  if (!csv || typeof csv !== 'string') {
    console.error('parseCSV: Invalid CSV input', { csv });
    return [];
  }
  
  try {
    // Normalize line endings (handle both Windows CRLF and Unix LF)
    const normalizedCsv = csv.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedCsv.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
  
    // Fields that should remain as strings (not converted to numbers)
    const stringFields = [
      'usage_id', 'module_id', 'zone_id', 'date',
      'measurement_level', 'site_type', 'site_name', 'zone_name',
      'equipment_name', 'equipment_category', 'product_family',
      'module_name', 'installation_type'
    ];
    
    return lines.slice(1).map(line => {
      const values = line.split(',');
      const obj: Record<string, string | number> = {};
      headers.forEach((header, index) => {
        const value = values[index]?.trim() || '';
        
        // Keep string fields as strings
        if (stringFields.includes(header)) {
          obj[header] = value;
        } else {
          // Try to convert to number for numeric fields
          const numValue = parseFloat(value);
          obj[header] = !isNaN(numValue) && value !== '' ? numValue : value;
        }
      });
      return obj as T;
    });
  } catch (error) {
    console.error('parseCSV: Error parsing CSV', error);
    return [];
  }
}

// Cache parsed data - reset on module reload
let cachedFactData: ModuleUsage[] | null = null;
let cachedDimData: ModuleDim[] | null = null;
let cachedZoneData: ZoneDim[] | null = null;

// Force cache reset on hot reload
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    cachedFactData = null;
    cachedDimData = null;
    cachedZoneData = null;
  });
}

function getFactData(): ModuleUsage[] {
  if (!cachedFactData) {
    cachedFactData = parseCSV<ModuleUsage>(factModuleUsageRaw);
    console.log('Parsed fact data:', cachedFactData.length, 'rows');
    console.log('Sample dates:', cachedFactData.slice(0, 5).map(d => d.date));
  }
  return cachedFactData;
}

function getDimData(): ModuleDim[] {
  if (!cachedDimData) {
    cachedDimData = parseCSV<ModuleDim>(dimModuleRaw);
  }
  return cachedDimData;
}

// Zone dimension data - available for drill-down functionality
export function getZoneData(): ZoneDim[] {
  if (!cachedZoneData) {
    cachedZoneData = parseCSV<ZoneDim>(dimZoneRaw);
  }
  return cachedZoneData;
}

// Filter data based on filters
// Maps filter fields to new hierarchical field names
function filterData(data: ModuleUsage[], filters: DemoFilters): ModuleUsage[] {
  console.log('filterData called with:', filters);
  console.log('Input data count:', data.length);
  
  const result = data.filter(d => {
    // sector filter → zone_name
    if (filters.sector && filters.sector !== '' && d.zone_name !== filters.sector) return false;
    // deviceType filter → equipment_category
    if (filters.deviceType && filters.deviceType !== '' && d.equipment_category !== filters.deviceType) return false;
    // siteType filter
    if (filters.siteType && filters.siteType !== '' && d.site_type !== filters.siteType) return false;
    // siteName filter
    if (filters.siteName && filters.siteName !== '' && d.site_name !== filters.siteName) return false;
    // measurementLevel filter
    if (filters.measurementLevel && filters.measurementLevel !== '' && d.measurement_level !== filters.measurementLevel) return false;
    // Date filters
    if (filters.startDate && filters.startDate !== '' && d.date < filters.startDate) return false;
    if (filters.endDate && filters.endDate !== '' && d.date > filters.endDate) return false;
    return true;
  });
  
  console.log('Filtered data count:', result.length);
  return result;
}

// Get filter options
export function getFilterOptions(): FilterOptions {
  const factData = getFactData();
  const dimData = getDimData();
  
  // sectors = unique zone_name values
  const sectors = [...new Set(factData.map(d => d.zone_name))].filter(Boolean).sort();
  // deviceTypes = unique equipment_category values
  const deviceTypes = [...new Set(factData.map(d => d.equipment_category))].filter(Boolean).sort();
  const siteTypes = [...new Set(dimData.map(d => d.site_type))].filter(Boolean).sort();
  const siteNames = [...new Set(dimData.map(d => d.site_name))].filter(Boolean).sort();
  const dates = factData.map(d => d.date).sort();
  
  return {
    sectors,
    deviceTypes,
    siteTypes,
    siteNames,
    dateRange: {
      min: dates[0] || '',
      max: dates[dates.length - 1] || '',
    },
  };
}

// Get KPIs
export function getKpis(filters: DemoFilters): DemoKpis {
  const data = filterData(getFactData(), filters);
  
  const totalEnergy = data.reduce((sum, d) => sum + d.energy_kwh, 0);
  const totalCost = data.reduce((sum, d) => sum + d.cost_eur, 0);
  const totalCo2 = data.reduce((sum, d) => sum + d.co2_kg, 0);
  const totalDuration = data.reduce((sum, d) => sum + d.duration_min, 0);
  const activeModules = new Set(data.map(d => d.module_id)).size;
  const avgCostPerKwh = totalEnergy > 0 ? totalCost / totalEnergy : 0;
  
  return {
    totalEnergy,
    totalCost,
    totalCo2,
    activeModules,
    avgCostPerKwh,
    totalDuration,
  };
}

// Get energy consumption over time
export function getEnergyByDate(filters: DemoFilters, includeForecast = false): EnergyByDate[] {
  const data = filterData(getFactData(), filters);
  
  console.log('getEnergyByDate - filtered data:', data.length, 'rows');
  console.log('getEnergyByDate - filters:', filters);
  
  // Group by date
  const byDate = new Map<string, { energy: number; cost: number; co2: number }>();
  data.forEach(d => {
    const dateKey = String(d.date); // Ensure it's a string
    const existing = byDate.get(dateKey) || { energy: 0, cost: 0, co2: 0 };
    byDate.set(dateKey, {
      energy: existing.energy + d.energy_kwh,
      cost: existing.cost + d.cost_eur,
      co2: existing.co2 + d.co2_kg,
    });
  });
  
  console.log('getEnergyByDate - unique dates:', byDate.size);
  
  // Convert to array and sort
  const result = Array.from(byDate.entries())
    .map(([date, values]) => ({
      date,
      ...values,
      isForecast: false,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
  
  console.log('getEnergyByDate - result points:', result.length);
  if (result.length > 0) {
    console.log('getEnergyByDate - first date:', result[0].date, 'last date:', result[result.length - 1].date);
  }
  
  // Add forecast if enabled
  if (includeForecast && result.length > 7) {
    const lastDays = result.slice(-7);
    const avgEnergy = lastDays.reduce((sum, d) => sum + d.energy, 0) / lastDays.length;
    const avgCost = lastDays.reduce((sum, d) => sum + d.cost, 0) / lastDays.length;
    const avgCo2 = lastDays.reduce((sum, d) => sum + d.co2, 0) / lastDays.length;
    
    // Add trend (slight increase)
    const trend = 1.02;
    
    const lastDate = new Date(result[result.length - 1].date);
    for (let i = 1; i <= 30; i++) {
      const forecastDate = new Date(lastDate);
      forecastDate.setDate(forecastDate.getDate() + i);
      const dateStr = forecastDate.toISOString().split('T')[0];
      
      // Add some randomness
      const randomFactor = 0.9 + Math.random() * 0.2;
      const trendFactor = Math.pow(trend, i / 10);
      
      result.push({
        date: dateStr,
        energy: avgEnergy * trendFactor * randomFactor,
        cost: avgCost * trendFactor * randomFactor,
        co2: avgCo2 * trendFactor * randomFactor,
        isForecast: true,
      });
    }
  }
  
  return result;
}

// Get consumption by sector (zone_name in hierarchical model)
// Returns data with simplified zone labels for executive view
export function getConsumptionBySector(filters: DemoFilters): ConsumptionBySector[] {
  const data = filterData(getFactData(), filters);
  
  // Group by zone_id to maintain unique zones
  const bySector = new Map<string, { zone_name: string; energy: number; cost: number; co2: number }>();
  data.forEach(d => {
    const zoneKey = d.zone_id || 'Unknown';
    const existing = bySector.get(zoneKey) || { zone_name: d.zone_name, energy: 0, cost: 0, co2: 0 };
    bySector.set(zoneKey, {
      zone_name: d.zone_name || translate('eq_unknown'),
      energy: existing.energy + d.energy_kwh,
      cost: existing.cost + d.cost_eur,
      co2: existing.co2 + d.co2_kg,
    });
  });
  
  // Convert to array and sort by energy
  const sorted = Array.from(bySector.entries())
    .map(([zone_id, values]) => ({ zone_id, ...values }))
    .sort((a, b) => b.energy - a.energy);
  
  // Generate clean zone labels (Zone 1, Zone 2, etc.)
  return sorted.map((item, index) => ({
    sector: item.zone_name,
    zone_id: item.zone_id,
    zoneLabel: translate('leg2_zone_n', { n: index + 1 }),
    energy: item.energy,
    cost: item.cost,
    co2: item.co2,
  }));
}

// Get consumption by device type (equipment_category in hierarchical model)
export function getConsumptionByDeviceType(filters: DemoFilters): ConsumptionByDeviceType[] {
  const data = filterData(getFactData(), filters);
  
  // Group by equipment_category (which maps to device_type in the UI)
  const byType = new Map<string, { energy: number; cost: number; co2: number }>();
  data.forEach(d => {
    const typeKey = d.equipment_category || translate('eq_unknown');
    const existing = byType.get(typeKey) || { energy: 0, cost: 0, co2: 0 };
    byType.set(typeKey, {
      energy: existing.energy + d.energy_kwh,
      cost: existing.cost + d.cost_eur,
      co2: existing.co2 + d.co2_kg,
    });
  });
  
  const result = Array.from(byType.entries())
    .map(([device_type, values]) => ({ device_type, ...values }))
    .sort((a, b) => b.energy - a.energy);
  
  // Calculate percentage
  const totalEnergy = result.reduce((sum, d) => sum + d.energy, 0);
  return result.map(d => ({
    ...d,
    percentage: totalEnergy > 0 ? (d.energy / totalEnergy) * 100 : 0,
  }));
}

// Get top consuming modules
export function getTopModules(filters: DemoFilters, limit = 10): TopModule[] {
  const data = filterData(getFactData(), filters);
  const dimData = getDimData();
  
  // Create lookup for module info
  const moduleInfo = new Map(dimData.map(d => [d.module_id, {
    module_name: d.module_name,
    zone_name: d.zone_name,
    equipment_category: d.equipment_category,
  }]));
  
  const byModule = new Map<string, { energy: number; cost: number; co2: number; zone_name: string; equipment_category: string }>();
  data.forEach(d => {
    const existing = byModule.get(d.module_id) || { 
      energy: 0, 
      cost: 0, 
      co2: 0, 
      zone_name: d.zone_name, 
      equipment_category: d.equipment_category 
    };
    byModule.set(d.module_id, {
      energy: existing.energy + d.energy_kwh,
      cost: existing.cost + d.cost_eur,
      co2: existing.co2 + d.co2_kg,
      zone_name: d.zone_name,
      equipment_category: d.equipment_category,
    });
  });
  
  return Array.from(byModule.entries())
    .map(([module_id, values]) => {
      const info = moduleInfo.get(module_id);
      return {
        module_id,
        module_name: info?.module_name || module_id,
        sector: values.zone_name,           // Map zone_name to sector for UI compatibility
        device_type: values.equipment_category, // Map equipment_category to device_type for UI compatibility
        energy: values.energy,
        cost: values.cost,
        co2: values.co2,
      };
    })
    .sort((a, b) => b.energy - a.energy)
    .slice(0, limit);
}

// Get CO2 by sector
export function getCo2BySector(filters: DemoFilters): ConsumptionBySector[] {
  return getConsumptionBySector(filters).sort((a, b) => b.co2 - a.co2);
}

// Identify anomalies (modules with unusually high consumption)
export function getAnomalies(filters: DemoFilters): TopModule[] {
  const topModules = getTopModules(filters, 20);
  
  if (topModules.length < 3) return [];
  
  const avgEnergy = topModules.reduce((sum, m) => sum + m.energy, 0) / topModules.length;
  const threshold = avgEnergy * 1.5;
  
  return topModules.filter(m => m.energy > threshold);
}

// ============================================
// HIERARCHICAL DRILL-DOWN FUNCTIONS
// Level 1: Zone, Level 2: Module, Level 3: Equipment
// ============================================

// Get top zones (Level 1 - aggregated by zone)
export function getTopZones(filters: DemoFilters, limit = 10): HierarchyItem[] {
  const data = filterData(getFactData(), filters);
  
  // Aggregate by zone_id
  const byZone = new Map<string, { zone_name: string; energy: number; cost: number; co2: number; moduleCount: number }>();
  data.forEach(d => {
    const existing = byZone.get(d.zone_id) || { zone_name: d.zone_name, energy: 0, cost: 0, co2: 0, moduleCount: 0 };
    byZone.set(d.zone_id, {
      zone_name: d.zone_name,
      energy: existing.energy + d.energy_kwh,
      cost: existing.cost + d.cost_eur,
      co2: existing.co2 + d.co2_kg,
      moduleCount: existing.moduleCount + 1,
    });
  });
  
  // Check which zones have modules
  const dimData = getDimData();
  const zonesWithModules = new Set(dimData.filter(d => d.measurement_level === 'Équipement').map(d => d.zone_id));
  
  return Array.from(byZone.entries())
    .map(([zone_id, values], index) => ({
      id: zone_id,
      name: values.zone_name,
      displayLabel: translate('leg2_zone_n', { n: index + 1 }),
      energy: values.energy,
      cost: values.cost,
      co2: values.co2,
      level: 'zone' as DrillLevel,
      hasChildren: zonesWithModules.has(zone_id),
    }))
    .sort((a, b) => b.energy - a.energy)
    .slice(0, limit);
}

// Get top modules within a zone (Level 2)
export function getTopModulesInZone(filters: DemoFilters, zoneId: string, limit = 10): HierarchyItem[] {
  const data = filterData(getFactData(), filters).filter(d => d.zone_id === zoneId);
  const dimData = getDimData();
  
  // Get zone name for parent info
  const zoneInfo = dimData.find(d => d.zone_id === zoneId);
  const zoneName = zoneInfo?.zone_name || zoneId;
  
  // Aggregate by module_id
  const byModule = new Map<string, { module_name: string; energy: number; cost: number; co2: number }>();
  data.forEach(d => {
    const existing = byModule.get(d.module_id) || { module_name: '', energy: 0, cost: 0, co2: 0 };
    const moduleInfo = dimData.find(m => m.module_id === d.module_id);
    byModule.set(d.module_id, {
      module_name: moduleInfo?.module_name || d.module_id,
      energy: existing.energy + d.energy_kwh,
      cost: existing.cost + d.cost_eur,
      co2: existing.co2 + d.co2_kg,
    });
  });
  
  // Check which modules have equipment
  const modulesWithEquipment = new Set(
    dimData.filter(d => d.zone_id === zoneId && d.measurement_level === 'Équipement').map(d => d.module_id)
  );
  
  return Array.from(byModule.entries())
    .map(([module_id, values]) => ({
      id: module_id,
      name: values.module_name,
      displayLabel: values.module_name,
      energy: values.energy,
      cost: values.cost,
      co2: values.co2,
      level: 'module' as DrillLevel,
      parentId: zoneId,
      parentName: zoneName,
      hasChildren: modulesWithEquipment.has(module_id),
    }))
    .sort((a, b) => b.energy - a.energy)
    .slice(0, limit);
}

// Get equipment within a module (Level 3)
export function getEquipmentInModule(filters: DemoFilters, moduleId: string, limit = 10): HierarchyItem[] {
  const data = filterData(getFactData(), filters).filter(d => 
    d.module_id === moduleId && d.measurement_level === 'Équipement'
  );
  const dimData = getDimData();
  
  // Get module info for parent
  const moduleInfo = dimData.find(d => d.module_id === moduleId);
  const moduleName = moduleInfo?.module_name || moduleId;
  
  // Aggregate by equipment_name
  const byEquipment = new Map<string, { energy: number; cost: number; co2: number }>();
  data.forEach(d => {
    const equipKey = d.equipment_name || translate('eq_unknown');
    const existing = byEquipment.get(equipKey) || { energy: 0, cost: 0, co2: 0 };
    byEquipment.set(equipKey, {
      energy: existing.energy + d.energy_kwh,
      cost: existing.cost + d.cost_eur,
      co2: existing.co2 + d.co2_kg,
    });
  });
  
  return Array.from(byEquipment.entries())
    .map(([equipment_name, values]) => ({
      id: `${moduleId}-${equipment_name}`,
      name: equipment_name,
      displayLabel: equipment_name,
      energy: values.energy,
      cost: values.cost,
      co2: values.co2,
      level: 'equipment' as DrillLevel,
      parentId: moduleId,
      parentName: moduleName,
      hasChildren: false,
    }))
    .sort((a, b) => b.energy - a.energy)
    .slice(0, limit);
}